import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "ghost-light";
type Size = "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F47920] disabled:opacity-50 disabled:cursor-not-allowed";

const variants: Record<Variant, string> = {
  primary: "bg-accent text-primary hover:bg-[var(--accent-hover)] hover:text-primary",
  secondary: "bg-primary text-white hover:bg-[var(--primary-hover)]",
  ghost:
    "border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-white",
  "ghost-light":
    "border-2 border-white/80 text-white bg-transparent hover:bg-white hover:text-primary",
};

const sizes: Record<Size, string> = {
  md: "px-6 py-3 text-[15px] min-h-[44px]",
  lg: "px-7 py-3.5 text-base min-h-[48px]",
};

export interface BtnProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
}

export const Button = forwardRef<
  HTMLButtonElement,
  BtnProps & ButtonHTMLAttributes<HTMLButtonElement>
>(({ variant = "primary", size = "md", className, children, ...props }, ref) => (
  <button ref={ref} className={cn(base, variants[variant], sizes[size], className)} {...props}>
    {children}
  </button>
));
Button.displayName = "Button";

export const ButtonLink = forwardRef<
  HTMLAnchorElement,
  BtnProps & AnchorHTMLAttributes<HTMLAnchorElement>
>(({ variant = "primary", size = "md", className, children, ...props }, ref) => (
  <a ref={ref} className={cn(base, variants[variant], sizes[size], className)} {...props}>
    {children}
  </a>
));
ButtonLink.displayName = "ButtonLink";
